/**
 * Visual indicator-based strategy builder (Streak-style).
 * Generates the same JSON DSL understood by backtest_engine and strategy_runner.
 * Supports inline backtesting before saving.
 */
import { useState } from "react";
import api from "@/lib/api";
import ScheduleBuilder from "@/components/strategy/ScheduleBuilder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, Play, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { toast } from "sonner";

// ── Constants ─────────────────────────────────────────────────────────────────

const SYMBOLS = [
  "NIFTY","BANKNIFTY","FINNIFTY","RELIANCE","TCS","HDFCBANK","INFY","ICICIBANK",
  "SBIN","AXISBANK","KOTAKBANK","BAJFINANCE","WIPRO","TATAMOTORS","MARUTI",
  "SUNPHARMA","LT","ADANIENT","TITAN","HCLTECH","HINDUNILVR","TATASTEEL",
  "GOLD","SILVER","CRUDEOIL","NATURALGAS","COPPER","ZINC",
];

const TIMEFRAMES = [
  { value:"1",  label:"1 min" },
  { value:"3",  label:"3 min" },
  { value:"5",  label:"5 min" },
  { value:"15", label:"15 min" },
  { value:"60", label:"1 hour" },
  { value:"1d", label:"Daily" },
];

const INDICATOR_TYPES = [
  { type:"sma",  label:"SMA",              fields:[{key:"period",label:"Period",default:20}] },
  { type:"ema",  label:"EMA",              fields:[{key:"period",label:"Period",default:20}] },
  { type:"rsi",  label:"RSI",              fields:[{key:"period",label:"Period",default:14}] },
  { type:"bb",   label:"Bollinger Bands",  fields:[{key:"period",label:"Period",default:20},{key:"std_dev",label:"Std Dev",default:2}] },
  { type:"macd", label:"MACD",             fields:[{key:"fast",label:"Fast",default:12},{key:"slow",label:"Slow",default:26},{key:"signal",label:"Signal",default:9}] },
  { type:"atr",  label:"ATR",              fields:[{key:"period",label:"Period",default:14}] },
  { type:"vwap", label:"VWAP",             fields:[] },
  { type:"adx",  label:"ADX",              fields:[{key:"period",label:"Period",default:14}] },
  { type:"stoch",label:"Stochastic",       fields:[{key:"period",label:"K Period",default:14},{key:"d_period",label:"D Period",default:3}] },
  { type:"obv",  label:"OBV",              fields:[] },
  { type:"cci",  label:"CCI",              fields:[{key:"period",label:"Period",default:20}] },
];

// Comparators supported by backtest_engine._eval_rule
const COMPARATORS = [
  { value:">",  label:">" },
  { value:"<",  label:"<" },
  { value:">=", label:"≥" },
  { value:"<=", label:"≤" },
  { value:"==", label:"=" },
];

const CANDLE_SERIES = [
  {value:"close",  label:"Close"},
  {value:"open",   label:"Open"},
  {value:"high",   label:"High"},
  {value:"low",    label:"Low"},
  {value:"volume", label:"Volume"},
];

const STATE_SERIES = [
  {value:"price_change_pct", label:"Price chg %"},
  {value:"time_elapsed",     label:"Bars in trade"},
  {value:"loss_streak",      label:"Loss streak"},
];

// ── Helpers ───────────────────────────────────────────────────────────────────

// FastAPI validation errors arrive as a list of {loc, msg}; show the message text, not an object.
function apiError(e, fallback) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) return d.map(x => String(x?.msg || x).replace(/^Value error, /, "")).join("; ");
  return fallback;
}

function makeIndicatorId(type, fields) {
  const period = fields.period ? `_${fields.period}` : "";
  return `${type}${period}`;
}

function operandOptions(indicators) {
  const inds = indicators.flatMap((ind) => {
    const base = ind.id;
    if (ind.type === "bb")    return [{value:`${base}_upper`,label:`BB Upper`},{value:`${base}_mid`,label:`BB Mid`},{value:`${base}_lower`,label:`BB Lower`}];
    if (ind.type === "macd")  return [{value:base,label:`MACD Line`},{value:`${base}_signal`,label:`MACD Signal`},{value:`${base}_hist`,label:`MACD Hist`}];
    if (ind.type === "stoch") return [{value:`${base}_k`,label:`Stoch %K`},{value:`${base}_d`,label:`Stoch %D`}];
    return [{value:base,label:`${ind.type.toUpperCase()}(${ind.period||""})`}];
  });
  return [
    ...CANDLE_SERIES.map(s=>({...s,group:"Price"})),
    ...inds.map(o=>({...o,group:"Indicators"})),
    ...STATE_SERIES.map(s=>({...s,group:"Trade state"})),
  ];
}

const fmtN = (v, d=2) => (v==null||isNaN(v)) ? "—" : Number(v).toLocaleString("en-IN",{minimumFractionDigits:d,maximumFractionDigits:d});

// ── Sub-components ────────────────────────────────────────────────────────────

function TFPicker({label, value, onChange}) {
  return (
    <div>
      <Label className="overline text-xs">{label}</Label>
      <div className="flex flex-wrap gap-1 mt-1">
        {TIMEFRAMES.map(tf=>(
          <button key={tf.value} type="button" onClick={()=>onChange(tf.value)}
            className={`text-xs px-2.5 py-1 border font-section tracking-wider transition-colors ${value===tf.value?"border-white text-white bg-[var(--bg-surface-2)]":"border-[var(--border)] txt-muted hover:border-white/40"}`}>
            {tf.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function OperandSelect({val, onChange, options}) {
  return (
    <select value={val||""} onChange={e=>onChange(e.target.value)}
      className="flex-1 min-w-0 bg-[var(--bg-page)] border border-[var(--border)] text-xs px-2 py-1.5 text-[var(--text-primary)] focus:outline-none focus:border-white/40">
      <option value="">Select…</option>
      {["Price","Indicators","Trade state"].map(grp=>{
        const items=options.filter(o=>o.group===grp);
        return items.length ? (
          <optgroup key={grp} label={grp}>
            {items.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
          </optgroup>
        ) : null;
      })}
    </select>
  );
}

function ConditionRow({cond, index, indicators, onUpdate, onRemove}) {
  const ops = operandOptions(indicators);
  // Determine if right side is numeric constant (not an indicator/series key)
  const rightIsNum = cond._numRight !== false && (cond.right==null || !ops.find(o=>o.value===String(cond.right)));

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-xs txt-muted w-5 text-right shrink-0">{index+1}.</span>

      <OperandSelect val={cond.left} onChange={v=>onUpdate({...cond,left:v})} options={ops} />

      <select value={cond.cmp||">"} onChange={e=>onUpdate({...cond,cmp:e.target.value})}
        className="w-16 bg-[var(--bg-page)] border border-[var(--border)] text-xs px-2 py-1.5 text-[var(--text-primary)] focus:outline-none focus:border-white/40 shrink-0">
        {COMPARATORS.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
      </select>

      {rightIsNum ? (
        <Input type="number" value={cond.right??""} onChange={e=>onUpdate({...cond,right:Number(e.target.value),_numRight:true})}
          placeholder="Value"
          className="w-24 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-xs h-[30px] shrink-0" />
      ) : (
        <OperandSelect val={String(cond.right||"")} onChange={v=>onUpdate({...cond,right:v,_numRight:false})} options={ops} />
      )}

      <button onClick={()=>onUpdate({...cond,right:rightIsNum?cond.right:"",_numRight:!rightIsNum})} type="button"
        title={rightIsNum?"Switch to indicator series":"Switch to numeric value"}
        className="text-[10px] px-1.5 py-1 border border-[var(--border)] txt-muted hover:border-white/40 shrink-0 whitespace-nowrap">
        {rightIsNum?"↔ Series":"↔ #"}
      </button>

      <button onClick={onRemove} type="button" className="text-zinc-500 hover:text-red-400 p-1 shrink-0">
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ConditionGroup({label, conditions, op, indicators, onAddCond, onUpdateCond, onRemoveCond, onToggleOp}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <Label className="overline text-xs">{label} conditions</Label>
        {conditions.length > 1 && (
          <button type="button" onClick={onToggleOp}
            className={`text-[10px] px-2 py-0.5 font-section border tracking-wider transition-colors ${op==="and"?"border-blue-500/60 text-blue-300":"border-amber-500/60 text-amber-300"}`}>
            {op.toUpperCase()} — click to toggle
          </button>
        )}
      </div>
      <div className="space-y-2 pl-2 border-l border-[var(--border)]">
        {conditions.length===0 && (
          <p className="text-xs txt-muted italic">No conditions — will always {label.toLowerCase()}.</p>
        )}
        {conditions.map((c,i)=>(
          <ConditionRow key={i} cond={c} index={i} indicators={indicators}
            onUpdate={v=>onUpdateCond(i,v)}
            onRemove={()=>onRemoveCond(i)} />
        ))}
      </div>
      <button type="button" onClick={onAddCond}
        className="flex items-center gap-1.5 text-xs txt-muted hover:text-[var(--text-primary)] border border-[var(--border)] hover:border-white/40 px-2 py-1 transition-colors">
        <Plus className="w-3 h-3" /> Add condition
      </button>
    </div>
  );
}

function BacktestSummary({result}) {
  if (!result) return null;
  const ret = result.total_return_pct ?? 0;
  const retPos = ret > 0;
  return (
    <div className="border border-[var(--border)] bg-[var(--bg-page)] p-4 space-y-3">
      <div className="flex items-center gap-2">
        {retPos ? <TrendingUp className="w-4 h-4 txt-profit" /> : <TrendingDown className="w-4 h-4 txt-loss" />}
        <span className="overline text-xs">Backtest result · {result.days || 180} days</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-center">
        {[
          {label:"Return",  val:`${ret>=0?"+":""}${fmtN(ret)}%`,      cls:retPos?"txt-profit":"txt-loss"},
          {label:"Win %",   val:`${fmtN(result.win_rate_pct)}%`,       cls:result.win_rate_pct>=50?"txt-profit":"txt-loss"},
          {label:"Sharpe",  val:fmtN(result.sharpe),                   cls:result.sharpe>=1?"txt-profit":"txt-muted"},
          {label:"Max DD",  val:`${fmtN(result.max_drawdown_pct)}%`,   cls:"txt-loss"},
          {label:"Trades",  val:result.total_trades,                   cls:""},
        ].map(m=>(
          <div key={m.label}>
            <div className={`font-mono-data font-bold text-sm ${m.cls}`}>{m.val}</div>
            <div className="overline text-[9px] txt-muted mt-0.5">{m.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function IndicatorStrategyBuilder({ onSaved }) {
  const [name,       setName]       = useState("My Indicator Strategy");
  const [symbol,     setSymbol]     = useState("NIFTY");
  const [entryTF,    setEntryTF]    = useState("5");
  const [exitTF,     setExitTF]     = useState("1");
  const [indicators, setIndicators] = useState([
    {type:"rsi", period:14, id:"rsi_14"},
    {type:"sma", period:20, id:"sma_20"},
  ]);
  const [newIndType,   setNewIndType]   = useState("ema");
  const [newIndFields, setNewIndFields] = useState({period:9});
  const [entryConds,   setEntryConds]   = useState([{left:"rsi_14",cmp:"<",right:30,_numRight:true}]);
  const [exitConds,    setExitConds]    = useState([{left:"rsi_14",cmp:">",right:70,_numRight:true}]);
  const [entryOp,      setEntryOp]      = useState("and");
  const [exitOp,       setExitOp]       = useState("or");
  const [size,         setSize]         = useState(1);
  const [slPct,        setSlPct]        = useState(1.5);
  const [tgtPct,       setTgtPct]       = useState(3.0);
  const [schedule,     setSchedule]     = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [btLoading,    setBtLoading]    = useState(false);
  const [btResult,     setBtResult]     = useState(null);

  const selectedIndMeta = INDICATOR_TYPES.find(t=>t.type===newIndType) || INDICATOR_TYPES[0];

  const addIndicator = () => {
    const fields = {...newIndFields};
    const id = makeIndicatorId(newIndType, fields);
    if (indicators.find(i=>i.id===id)) {
      toast.error(`Indicator ${id} already added`); return;
    }
    setIndicators(prev=>[...prev, {type:newIndType, ...fields, id}]);
  };

  const removeIndicator = (id) => {
    setIndicators(prev=>prev.filter(i=>i.id!==id));
  };

  const buildDsl = () => {
    const buildRule = (conds, op) => ({
      op,
      rules: conds.map(c => {
        const right = (c._numRight !== false && typeof c.right !== "string")
          ? Number(c.right)
          : c.right;
        return {left: c.left, cmp: c.cmp, right};
      }),
    });
    return {
      name,
      symbol,
      entry_timeframe:  entryTF,
      exit_timeframe:   exitTF,
      indicators:       indicators.map(({id,type,...rest}) => ({id,type,...rest})),
      entry:            buildRule(entryConds, entryOp),
      exit:             buildRule(exitConds, exitOp),
      size:             {type:"fixed", value:Number(size)},
      stop_loss_pct:    Number(slPct),
      take_profit_pct:  Number(tgtPct),
      ...(schedule ? {schedule} : {}),
      description: `Indicator strategy: ${indicators.map(i=>i.id).join(", ")}`,
    };
  };

  const runBacktest = async () => {
    const dsl = buildDsl();
    if (!dsl.symbol) { toast.error("Select a symbol first"); return; }
    setBtLoading(true);
    setBtResult(null);
    try {
      const { data } = await api.post("/backtest/run", {
        dsl, capital: 500000, slippage_bps: 5, fee_bps: 2, days: 180, save: false,
      });
      setBtResult(data);
      toast.success("Backtest complete");
    } catch(e) {
      toast.error(apiError(e, "Backtest failed"));
    } finally {
      setBtLoading(false);
    }
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Strategy name required"); return; }
    setSaving(true);
    try {
      const dsl = buildDsl();
      await api.post("/strategies", {name, description: dsl.description, dsl});
      toast.success("Indicator strategy saved");
      setBtResult(null);
      onSaved?.();
    } catch(e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Strategy meta */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <Label className="overline">Strategy name</Label>
          <Input value={name} onChange={e=>setName(e.target.value)}
            className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-sm" />
        </div>
        <div>
          <Label className="overline">Symbol</Label>
          <select value={symbol} onChange={e=>setSymbol(e.target.value)}
            className="mt-1 w-full bg-[var(--bg-page)] border border-[var(--border)] text-sm px-3 py-2 text-[var(--text-primary)] focus:outline-none focus:border-white/40">
            {SYMBOLS.map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="overline">Qty</Label>
            <Input type="number" min={1} value={size} onChange={e=>setSize(e.target.value)}
              className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-sm" />
          </div>
          <div>
            <Label className="overline">SL %</Label>
            <Input type="number" step="0.1" value={slPct} onChange={e=>setSlPct(e.target.value)}
              className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-sm" />
          </div>
          <div>
            <Label className="overline">Tgt %</Label>
            <Input type="number" step="0.1" value={tgtPct} onChange={e=>setTgtPct(e.target.value)}
              className="mt-1 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-sm" />
          </div>
        </div>
      </div>

      {/* Timeframes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <TFPicker label="Entry timeframe" value={entryTF} onChange={setEntryTF} />
        <TFPicker label="Exit timeframe"  value={exitTF}  onChange={setExitTF} />
      </div>

      {/* Indicators */}
      <div className="space-y-3">
        <Label className="overline">Indicators</Label>
        <div className="flex flex-wrap gap-2">
          {indicators.map(ind=>(
            <div key={ind.id} className="flex items-center gap-1.5 px-2.5 py-1 border border-[var(--border)] bg-[var(--bg-surface)] text-xs">
              <span className="font-section text-[var(--text-primary)]">{ind.id.toUpperCase()}</span>
              {ind.period && <span className="txt-muted">({ind.period})</span>}
              <button onClick={()=>removeIndicator(ind.id)} type="button" className="text-zinc-500 hover:text-red-400 ml-1">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
          {indicators.length === 0 && <span className="text-xs txt-muted italic">No indicators added</span>}
        </div>
        {/* Add indicator row */}
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <span className="text-xs txt-muted block mb-1">Type</span>
            <select value={newIndType} onChange={e=>{setNewIndType(e.target.value); setNewIndFields({});}}
              className="bg-[var(--bg-page)] border border-[var(--border)] text-xs px-2 py-1.5 text-[var(--text-primary)] focus:outline-none focus:border-white/40">
              {INDICATOR_TYPES.map(t=><option key={t.type} value={t.type}>{t.label}</option>)}
            </select>
          </div>
          {selectedIndMeta.fields.map(f=>(
            <div key={f.key}>
              <span className="text-xs txt-muted block mb-1">{f.label}</span>
              <Input type="number" value={newIndFields[f.key]??f.default}
                onChange={e=>setNewIndFields(p=>({...p,[f.key]:Number(e.target.value)}))}
                className="w-20 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data text-xs h-[30px]" />
            </div>
          ))}
          <button type="button" onClick={addIndicator}
            className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 border border-[var(--border)] txt-muted hover:border-white/40 hover:text-[var(--text-primary)] transition-colors">
            <Plus className="w-3 h-3" /> Add indicator
          </button>
        </div>
      </div>

      {/* Entry conditions */}
      <ConditionGroup
        label="Entry"
        conditions={entryConds}
        op={entryOp}
        indicators={indicators}
        onAddCond={()=>setEntryConds(p=>[...p,{left:"close",cmp:">",right:0,_numRight:true}])}
        onUpdateCond={(i,v)=>setEntryConds(p=>{const n=[...p];n[i]=v;return n;})}
        onRemoveCond={(i)=>setEntryConds(p=>p.filter((_,j)=>j!==i))}
        onToggleOp={()=>setEntryOp(v=>v==="and"?"or":"and")}
      />

      {/* Exit conditions */}
      <ConditionGroup
        label="Exit"
        conditions={exitConds}
        op={exitOp}
        indicators={indicators}
        onAddCond={()=>setExitConds(p=>[...p,{left:"close",cmp:"<",right:0,_numRight:true}])}
        onUpdateCond={(i,v)=>setExitConds(p=>{const n=[...p];n[i]=v;return n;})}
        onRemoveCond={(i)=>setExitConds(p=>p.filter((_,j)=>j!==i))}
        onToggleOp={()=>setExitOp(v=>v==="and"?"or":"and")}
      />

      {/* Scheduler */}
      <div className="pt-4 border-t border-[var(--border)]">
        <ScheduleBuilder value={schedule} onChange={setSchedule} />
      </div>

      {/* DSL Preview */}
      <details className="text-xs">
        <summary className="cursor-pointer txt-muted hover:text-[var(--text-primary)] select-none">Preview generated DSL</summary>
        <pre className="mt-2 p-3 bg-[var(--bg-page)] border border-[var(--border)] overflow-auto max-h-64 txt-secondary font-mono-data text-[11px]">
          {JSON.stringify(buildDsl(),null,2)}
        </pre>
      </details>

      {/* Backtest result */}
      {btResult && <BacktestSummary result={btResult} />}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button onClick={runBacktest} disabled={btLoading} variant="outline"
          className="rounded-none border-white text-white hover:bg-white hover:text-black font-section tracking-wider h-10">
          {btLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          {btLoading ? "RUNNING…" : "RUN BACKTEST"}
        </Button>
        <Button onClick={save} disabled={saving}
          className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider h-10">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {saving ? "SAVING…" : "SAVE STRATEGY"}
        </Button>
      </div>
    </div>
  );
}
